import { useAuth } from "../../authentication/contexts/useAuth";
import React, { useEffect, useMemo, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import findTrackdechetsErrors from "../graphql/queries/findTrackdechetsErrors.graphql";
import triggerTrackdechetsAction from "../graphql/mutations/triggerTrackdechetsAction.graphql";
import { arrayToObject, removeElement } from "../../common/utils/arrayUtils";
import I18n from "i18n-js";
import PropTypes from "prop-types";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { TrackdechetsErrorCell } from "./TrackdechetsErrorCell";
import StatelessTable from "../../common/components/elements/StatelessTable";

TableWithTrackdechetsErrors.propTypes = {
  allData: PropTypes.arrayOf(PropTypes.shape().isRequired).isRequired,
  uuidKey: PropTypes.string.isRequired,
  target: PropTypes.oneOf(["INTERVENTION", "SHIPPING"]).isRequired,
  showTrackdechetsErrorsByDefault: PropTypes.bool,
  showTrackdechetsToggle: PropTypes.bool,
  ...StatelessTable.propTypes,
};

export function TableWithTrackdechetsErrors({
  allData = [],
  target,
  uuidKey = "uuid",
  showTrackdechetsErrorsByDefault = false,
  showTrackdechetsToggle = true,
  ...props
}) {
  const { user } = useAuth();
  const usesTrackdechets = user?.usesTrackdechets;
  const [showTrackdechetsErrors, setShowTrackdechetsErrors] = useState(
    showTrackdechetsErrorsByDefault,
  );

  const [fetch, { data, loading, error, refetch }] = useLazyQuery(
    findTrackdechetsErrors,
    {
      variables: {
        // fetch the statuses for the whole table data:
        uuids: allData.map((item) => item[uuidKey]),
        target,
      },
      notifyOnNetworkStatusChange: true,
    },
  );

  const refreshErrors = async () => {
    const variables = {
      uuids: allData.map((item) => item[uuidKey]),
      target,
    };

    if (refetch) {
      await refetch({
        ...variables,
        fetchPolicy: "network-only",
      });
      return;
    }

    fetch({ variables });
  };

  // fetch Trackdéchets sync statuses only once, once required:
  useEffect(() => {
    if (usesTrackdechets && showTrackdechetsErrors) {
      fetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTrackdechetsErrors, usesTrackdechets]);

  const { refreshError, isItemRefreshing, hasItemError } = useErrorRefresh(
    target,
    refetch,
  );

  const trackdechetsHeader = (
    <span
      className="d-inline-flex align-items-center"
      key="trackdechets-errors-header"
    >
      <span>
        {I18n.t(
          `trackdechets.sync_statuses_table.headers.${
            target === "INTERVENTION" ? "sync" : "error"
          }`,
        )}
      </span>
      <OverlayTrigger
        trigger={["focus", "hover"]}
        placement="top"
        delay={{ show: 10, hide: 150 }}
        overlay={
          <Tooltip style={{ position: "fixed" }}>
            {I18n.t("trackdechets.sync_statuses_table.refresh_statuses_tooltip")}
          </Tooltip>
        }
      >
        <button
          type="button"
          className="btn refresh"
          onClick={refreshErrors}
          disabled={loading}
          title={I18n.t("trackdechets.sync_statuses_table.refresh")}
          aria-label={I18n.t("trackdechets.sync_statuses_table.refresh")}
        >
          <i className="fa-solid fa-refresh" />
        </button>
      </OverlayTrigger>
    </span>
  );

  const headers = !showTrackdechetsErrors
    ? props.headData
    : props.headData.concat([trackdechetsHeader]);

  const trackdechetsErrors = useMemo(
    () => arrayToObject(data?.trackdechetsError || [], "uuid"),
    [data],
  );

  if (!usesTrackdechets) {
    return <StatelessTable {...props} />;
  }

  return (
    <StatelessTable
      {...props}
      headData={headers}
      actions={
        <>
          {props.actions}
          {showTrackdechetsToggle && (
            <>
              <br />
              <button
                type="button"
                className="btn btn-xs btn-link mt-2 p-0 text-primary"
                onClick={() =>
                  setShowTrackdechetsErrors(!showTrackdechetsErrors)
                }
              >
                {I18n.t(
                  `trackdechets.sync_statuses_table.${
                    showTrackdechetsErrors ? "hide" : "show"
                  }`,
                )}
              </button>
            </>
          )}
        </>
      }
      rowData={(row, index) => {
        const cells = props.rowData(row, index);

        if (!showTrackdechetsErrors) {
          return cells;
        }

        const uuid = row.uuid;
        const trackdechetsError = trackdechetsErrors[uuid] || null;
        const syncStatus = target === "INTERVENTION" ? row.trackdechetsSynced : null;

        return cells.concat([
          <TrackdechetsErrorCell
            key="td-sync-status-cell"
            loading={loading || isItemRefreshing(uuid)}
            error={error || hasItemError(uuid)}
            message={trackdechetsError?.message}
            syncStatus={syncStatus}
            showSyncStatus={target === "INTERVENTION"}
            createdAt={
              trackdechetsError?.createdAt
                ? new Date(trackdechetsError.createdAt)
                : null
            }
            refresh={() => refreshError(uuid)}
          />,
        ]);
      }}
    />
  );
}

/**
 * Hook providing logic for refreshing a single status item in the list,
 * by triggering an async update and waiting a bit before fetching the new value.
 */
function useErrorRefresh(target, refetch) {
  const [refreshingRows, setRefreshingRows] = useState([]);
  const [errors, setErrors] = useState([]);
  const [triggerUpdate] = useMutation(triggerTrackdechetsAction);
  // const [fetch] = useLazyQuery(findTrackdechetsErrors, {
  //   fetchPolicy: "network-only", // No cache to force refresh of the Apollo cache
  // });

  return {
    isItemRefreshing: (uuid) => refreshingRows.includes(uuid),
    hasItemError: (uuid) => errors.includes(uuid),
    refreshError: async (uuid) => {
      setRefreshingRows(refreshingRows.concat(uuid));
      setErrors(removeElement(errors, uuid));

      const options = {
        variables: {
          uuids: [uuid],
          target,
        },
      };

      try {
        // trigger async update of the status
        await triggerUpdate(options);

        // wait a second
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // attempt to fetch the updated result,
        // which will update Apollo's cached data for this item:
        await refetch({
          ...options,
          fetchPolicy: "network-only", // No cache to force refresh of the Apollo cache
        });
      } catch {
        setErrors(errors.concat(uuid));
      } finally {
        setRefreshingRows(removeElement(refreshingRows, uuid));
      }
    },
  };
}
